import { getImageModelSettings } from "./config/model-settings";

export type AIModel =
	| "v1.0"
	| "v2.0"
	| "v3.0"
	| "nano-banana-2"
	| "nano-banana-pro"
	| "gpt-image-2"
	| "nano-banana-2-1"
	| "qwen-image-3"
	| "qwen-image-3-pro"
	| "seedream-5-pro"
	| "seedream-5-flash"
	| "background-remover";
/**
 * /image-generator 真正接受的模型。"background-remover" 是模板专用的伪模型，
 * 走 behavior.customApiEndpoint 打 /api/background-remover/remove，
 * 从来不经过图片生成路由——后端的 MODEL_CONFIGS 里也没有它。
 */
export type ImageGeneratorApiModel = Exclude<AIModel, "background-remover">;

/**
 * 已下架模型 → 替代模型。历史记录行、IndexedDB 里的待恢复状态、深链参数都可能
 * 带着旧 id，而请求 schema 现在是严格 enum，不映射就会直接 400。
 */
const RETIRED_MODEL_REPLACEMENTS: Record<string, ImageGeneratorApiModel> = {
	"nano-banana": "nano-banana-2",
};

export function coerceRetiredModel(model: string): ImageGeneratorApiModel {
	return (RETIRED_MODEL_REPLACEMENTS[model] ??
		model) as ImageGeneratorApiModel;
}

/**
 * 付费专属模型被拦时的付费墙 reason。Nano Banana Pro 有专属文案，其余的
 * （qwen3 pro / seedream 5 pro）走通用「升级以解锁此功能」。
 */
export function paidModelPaywallReason(model: string) {
	return model === "nano-banana-pro" ? "nano-banana-pro" : "paid-model";
}

export type ImageResolution = "1K" | "2K" | "4K";

const RESOLUTION_PARAMETER_MODEL_IDS = [
	"v2.0",
	"v3.0",
	"nano-banana-2",
	"nano-banana-pro",
	"gpt-image-2",
	"nano-banana-2-1",
	"qwen-image-3",
	"qwen-image-3-pro",
	"seedream-5-pro",
	"seedream-5-flash",
] as const;

const RESOLUTION_SELECTOR_MODEL_IDS = [
	"v3.0",
	"nano-banana-2",
	"nano-banana-pro",
	"gpt-image-2",
	"nano-banana-2-1",
	"qwen-image-3",
	"qwen-image-3-pro",
	"seedream-5-pro",
	"seedream-5-flash",
] as const;

/**
 * v3.0（Seedream 5 Lite）的上游档位是 basic/high/ultra = 2K/3K/4K，跟站内的
 * 1K/2K/4K 值对不上。站内值一律不动（积分表、请求 schema、历史记录都挂在它
 * 上面），只在渲染时换 label —— 否则用户会看到选了 "1K" 却出 2K 图。
 */
const RESOLUTION_LABELS_BY_MODEL: Partial<
	Record<AIModel, Record<ImageResolution, { label: string; pixels: string }>>
> = {
	"v3.0": {
		"1K": { label: "2K", pixels: "2048px" },
		"2K": { label: "3K", pixels: "3072px" },
		"4K": { label: "4K", pixels: "4096px" },
	},
};

const DEFAULT_RESOLUTION_LABELS: Record<
	ImageResolution,
	{ label: string; pixels: string }
> = {
	"1K": { label: "1K", pixels: "1024px" },
	"2K": { label: "2K", pixels: "2048px" },
	"4K": { label: "4K", pixels: "4096px" },
};

export function getResolutionLabel(
	model: string | null | undefined,
	resolution: ImageResolution,
): { label: string; pixels: string } {
	return (
		RESOLUTION_LABELS_BY_MODEL[model as AIModel]?.[resolution] ??
		DEFAULT_RESOLUTION_LABELS[resolution]
	);
}

/**
 * 支持 aspect_ratio: "auto" 的模型 —— 目前正好是走 kie 的那三个，它们的上游
 * 枚举里有 auto。fal 的 v1.0/v2.0/v3.0 要的是具体像素尺寸，收到 "auto" 会在
 * convertAspectRatioToImageSize 里静默 fallback 成正方形，所以不能给它们开。
 */
export function supportsAutoAspectRatio(model?: string | null): boolean {
	return getImageModelSettings(model).aspectRatios.includes("auto");
}

export function supportsImageResolution(model?: string | null): boolean {
	return RESOLUTION_PARAMETER_MODEL_IDS.includes(
		model as (typeof RESOLUTION_PARAMETER_MODEL_IDS)[number],
	);
}

export function showsImageResolutionSelector(model?: string | null): boolean {
	return RESOLUTION_SELECTOR_MODEL_IDS.includes(
		model as (typeof RESOLUTION_SELECTOR_MODEL_IDS)[number],
	);
}

export type ImageStyle =
	| "none"
	| "photorealistic"
	| "pixar-art"
	| "anime"
	| "comic"
	| "lego"
	| "cinematic"
	| "watercolor";

export interface ModelConfig {
	credits: number;
	name: string;
	// Per-resolution price overrides, sourced from the backend /models endpoint.
	// The frontend never re-hardcodes these — it reads them from here.
	creditCostByResolution?: Record<string, number>;
	// 图生图每张加价，来自后端 IMAGE_TO_IMAGE_SURCHARGE。
	imageToImageSurcharge?: number;
	// 整个模型只给付费用户，来自后端 PAID_ONLY_MODELS。同样只读不硬编码。
	paidOnly?: boolean;
}

// 静态模型配置，作为 /models 拉取前的兜底。creditCostByResolution 和 paidOnly 必须
// 跟着后端的 CREDIT_COST_OVERRIDES / PAID_ONLY_MODELS 一起改：少了前者首屏会把
// 2K/4K 的加价少报成 1K 价，少了后者首屏会把付费专属模型显示成可选。
// 运行时仍以后端返回为准。
export const DEFAULT_MODEL_CONFIGS: Record<AIModel, ModelConfig> = {
	"v1.0": {
		credits: 1,
		name: "v1.0",
	},
	"v2.0": {
		credits: 2,
		name: "v2.0",
	},
	"v3.0": {
		credits: 6,
		name: "v3.0",
	},
	"nano-banana-2": {
		credits: 5,
		name: "nano-banana-2",
		creditCostByResolution: { "2K": 8, "4K": 12 },
	},
	"nano-banana-pro": {
		credits: 8,
		name: "nano-banana-pro",
		creditCostByResolution: { "4K": 14 },
		paidOnly: true,
	},
	"gpt-image-2": {
		credits: 3,
		name: "gpt-image-2",
		creditCostByResolution: { "2K": 5, "4K": 8 },
	},
	"nano-banana-2-1": {
		credits: 4,
		name: "nano-banana-2-1",
		creditCostByResolution: { "2K": 6, "4K": 9 },
	},
	"qwen-image-3": {
		credits: 5,
		name: "qwen-image-3",
		imageToImageSurcharge: 1,
	},
	"qwen-image-3-pro": {
		credits: 7,
		name: "qwen-image-3-pro",
		creditCostByResolution: { "2K": 12 },
		imageToImageSurcharge: 1,
		paidOnly: true,
	},
	"seedream-5-pro": {
		credits: 7,
		name: "seedream-5-pro",
		creditCostByResolution: { "2K": 14 },
		imageToImageSurcharge: 1,
		paidOnly: true,
	},
	"seedream-5-flash": {
		credits: 4,
		name: "seedream-5-flash",
	},
	"background-remover": {
		credits: 1,
		name: "background-remover",
	},
};

export function getImageGenerationCreditCost(
	model: AIModel,
	resolution?: ImageResolution | string,
	imageCount = 1,
	modelConfigs: Record<AIModel, ModelConfig> = DEFAULT_MODEL_CONFIGS,
	isImageToImage = false,
): number {
	const config = modelConfigs[model];
	const base = config?.credits || 1;
	const override = resolution
		? config?.creditCostByResolution?.[resolution]
		: undefined;
	const surcharge = isImageToImage ? (config?.imageToImageSurcharge ?? 0) : 0;

	return ((override ?? base) + surcharge) * imageCount;
}

/**
 * 免费用户的高清档降级，判据与后端 clampResolutionForPlan 一致：只要这一档
 * 出现在 creditCostByResolution 里（也就是比 1K 贵），免费用户就落回 1K。
 *
 * 下拉框里的档位有 lockedResolutions 挡着，但历史记录/My Assets 的重新生成
 * 直接读旧记录里的档位，绕开了下拉框，只能在发请求前再算一次。前端不算的话，
 * 费用预检会按 4K 的价拦下明明有 1K 额度的用户，乐观记录也会先显示 4K 再被
 * 后端改成 1K。
 */
export function clampResolutionForPlan<T extends ImageResolution | string>(
	model: AIModel,
	resolution: T,
	isPaid: boolean,
	modelConfigs: Record<AIModel, ModelConfig> = DEFAULT_MODEL_CONFIGS,
): T | "1K" {
	if (isPaid) {
		return resolution;
	}
	return modelConfigs[model]?.creditCostByResolution?.[resolution]
		? "1K"
		: resolution;
}

/**
 * 模型下拉每行的价：切到这个模型后 Create 上会显示多少。档位照切换后的规则推：
 * 这个模型没有当前档就落到它的最高档（use-ai-image-generator 里的 effect），
 * 免费用户碰上加价档再落回 1K（组件里的 lockedResolutions）。
 */
export function getCreditCostAfterSwitch(
	model: AIModel,
	resolution: ImageResolution,
	imageCount: number,
	modelConfigs: Record<AIModel, ModelConfig>,
	isImageToImage: boolean,
	isPaid: boolean,
): number {
	let nextResolution: ImageResolution = resolution;
	if (supportsImageResolution(model)) {
		const allowed = getImageModelSettings(model).resolutions;
		if (!allowed.includes(resolution)) {
			nextResolution = allowed[allowed.length - 1];
		}
	}
	return getImageGenerationCreditCost(
		model,
		clampResolutionForPlan(model, nextResolution, isPaid, modelConfigs),
		imageCount,
		modelConfigs,
		isImageToImage,
	);
}

export interface PromptVersion {
	prompt: string;
	timestamp: number;
}

export interface PromptHistoryState {
	versions: PromptVersion[];
	currentIndex: number;
}

export interface ImageGenerationResponse {
	imageUrls: string[]; // 生成的图片URL数组（单张或多张）
	model: string;
	prompt: string;
	originalPrompt: string;
	translatedPrompt?: string;
	detectedLanguage?: string;
	wasTranslated: boolean;
	inputType: "text" | "image";
	inputImageUrls?: string[]; // 用户上传的原始图片URL数组
	isPublic?: boolean;
	hasWatermark?: boolean;
	metadata: {
		timestamp: string;
		parameters: {
			model: string;
			num_outputs?: number;
			aspect_ratio?: string;
			output_format?: string;
			output_quality?: number;
			num_inference_steps?: number;
			resolution?: ImageResolution;
			// Note: image URLs are stored in inputImageUrls field, not here
		};
		provider?: {
			type: string;
			modelId: string;
			parameters: any;
		};
	};
}

export type TemplateCategory = "all" | "personal";

export interface Template {
	id: string;
	name: string;
	description: string;
	prompt: string;
	/**
	 * 输入框的灰字提示。只在 prompt 留空的模板上用——prompt 会被预填进输入框的
	 * value，写在那里的引导文案会被当成真 prompt 发给上游。缺省用通用文案。
	 */
	promptPlaceholder?: string;
	category: TemplateCategory;
	thumbnail: string;
	exampleImages?: string[];
	/**
	 * 内页的时效信号，ISO 日期（YYYY-MM-DD）。喂给 JSON-LD 的
	 * datePublished / dateModified。原来还在页尾渲染成可见的「Last updated」（GEO 判据要求
	 * 两者同时存在），2026-10-25 老公定页面上不显示，只留 JSON-LD。
	 *
	 * **必须是真实日期**：现有 6 个模板的值取自 git 历史（首次出现 / 最后一次
	 * 改动，含 i18n 文案）。改了页面文案就把 updatedAt 一起改，不要放着不管——
	 * 一个长期不动的 dateModified 比没有更糟。
	 */
	publishedAt?: string;
	updatedAt?: string;
	// Optional marketing content for template-specific pages
	marketingContent?: {
		hideTestimonials?: boolean;
		features?: Array<{ title: string; description: string; icon?: string }>;
		useCases?: Array<{
			titleKey: string;
			descriptionKey: string;
			icon?: string;
			iconColor?: string;
			bgGradient?: string;
			/** 有这一项的场景卡照 /image-to-image 画成 Before/After + 出图 prompt（10/25 姿势页先做，6 张都在本页出的） */
			example?: { before: string; after: string; prompt?: string };
		}>;
		faqItems?: Array<{ questionKey: string; answerKey: string }>;
		examples?: Array<{ reference: string; result: string; prompt: string }>;
		carouselSlides?: Array<{
			referenceImages: string[];
			resultImage: string;
			prompt: string;
		}>;
		testimonials?: Array<{
			nameKey: string;
			roleKey: string;
			contentKey: string;
			avatarUrl?: string;
			rating?: number;
		}>;
		ctaTitleKey?: string;
		ctaDescriptionKey?: string;
		howItWorks?: Array<{
			step: number;
			titleKey: string;
			descriptionKey: string;
		}>;
	};
	// Template behavior configuration
	behavior?: {
		// Default aspect ratio (if user selects "original", auto-switch to this value)
		defaultAspectRatio?: string;
		// Whether to disable "original" aspect ratio option
		disableOriginalAspectRatio?: boolean;
		// Whether to show custom preview component (for non-logged-in users)
		showCustomPreview?: boolean;
		// Custom preview component configuration
		customPreview?: {
			// Preview image configuration
			images: {
				reference: string[]; // Array of reference images
				result: string; // Result image
			};
			// Preview component style configuration (optional)
			style?: {
				gradient?: string; // Background gradient
				theme?: "romantic" | "professional" | "creative"; // Preset theme
			};
		};
		// Model configuration
		model?: {
			// Default model (will be set when template is selected)
			default: AIModel;
			// Allowed models (optional, if specified, users can only choose from these models)
			allowed?: AIModel[];
		};
		// Resolution configuration for templates that support resolution selection
		resolution?: {
			// Default resolution shown when the template is selected
			default?: ImageResolution;
			// Allowed resolutions shown in the resolution dropdown
			allowed?: ImageResolution[];
		};
		// Maximum number of input images allowed (default: 2)
		maxImages?: number;
		// Whether to use custom API endpoint instead of default image-generator
		useCustomApi?: boolean;
		// Custom API endpoint path (e.g., "/api/background-remover/remove")
		customApiEndpoint?: string;
		// Credit cost for this template (overrides model default)
		creditCost?: number;
		// 配了 maxImages 也不强制传图：不传就按 prompt 文生图（脚图页 10/29）
		optionalImage?: boolean;
		// Hide prompt input for templates that don't need user prompt (e.g., background remover)
		hidePrompt?: boolean;
		// Hide aspect ratio selector for templates that preserve original aspect ratio
		hideAspectRatio?: boolean;
		// Hide resolution selector for templates with a fixed default resolution
		hideResolution?: boolean;
		// Hide output count selector for templates that only support single output
		hideOutputCount?: boolean;
		// prompt 上方的预设胶囊（肌肉页 10/29）：点一下把这句填进输入框，用户还能接着改。
		// 默认选中的那句和 template.prompt 写成同一句
		promptPresets?: Array<{ label: string; prompt: string }>;
		// 比例跟随输入图（大臀页 10/29）：模型上游没有 auto（Seedream 5 Flash）时，按第一张图
		// 就近吸附到模型支持的比例，否则全身照会被出成 1:1
		aspectRatioFollowsInput?: boolean;
	};
}
